// UserViewStage2.js - Add applicationId as prop
import React, { useState, useEffect } from "react";
import {
  FaBriefcase,
  FaGraduationCap,
  FaCertificate,
  FaAward,
  FaBook,
  FaUsers,
  FaFileAlt,
  FaIdCard,
  FaTools,
  FaUserTie,
  FaBuilding,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
  FaLink,
  FaUserCircle,
  FaShieldAlt,
  FaGlobe,
  FaChevronDown,
  FaChevronUp,
  FaStar,
  FaChartLine,
  FaMedal,
  FaTrophy,
  FaCode,
  FaLanguage,
  FaCog,
  FaExternalLinkAlt,
  FaEye,
} from "react-icons/fa";
import axios from "axios";
import { API_BASE } from "../../../ApiUrl";
import "./UserViewStage2.css";
import StatusApprovalStage2 from "./StatusApprovalStage2";

// Add applicationId to props
const UserViewStage2 = ({ professionalId, applicationId, onRefresh }) => {
  const [stage2Data, setStage2Data] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [expandedSections, setExpandedSections] = useState({
    employment: true,
    projects: false,
    credentials: false,
    capabilities: false,
    contacts: false,
    reviews: false,
  });
  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    if (professionalId) {
      fetchStage2Data(professionalId);
    }
  }, [professionalId]);

  const fetchStage2Data = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE}/professional-profiles/${id}/related-records/`,
      );
      if (response.data.success) {
        setStage2Data(response.data.data);
        console.log("Stage 2 Data:", response.data.data);
      } else {
        setError("Failed to load Stage 2 data");
      }
    } catch (err) {
      console.error("Error fetching Stage 2 data:", err);
      setError("Error loading Stage 2 details");
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleCardDetail = (cardId) => {
    setSelectedCard(selectedCard === cardId ? null : cardId);
  };

  const handleRefresh = () => {
    if (professionalId) {
      fetchStage2Data(professionalId);
    }
    if (onRefresh) {
      onRefresh();
    }
  };

  if (loading) {
    return (
      <div className="stage2-loading">
        <div className="stage2-loading-spinner">
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
        </div>
        <p>Loading professional records...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stage2-error-container">
        <div className="error-icon">⚠️</div>
        <p className="stage2-error-text">{error}</p>
        <button
          onClick={() => fetchStage2Data(professionalId)}
          className="stage2-retry-btn"
        >
          <FaCog className="retry-icon" /> Try Again
        </button>
      </div>
    );
  }

  if (!stage2Data) {
    return (
      <div className="stage2-empty-state">
        <div className="empty-icon-wrapper">
          <FaFileAlt className="empty-icon" />
        </div>
        <p>No Stage 2 records found</p>
        <span className="empty-subtext">
          Complete your professional profile to unlock this section
        </span>
      </div>
    );
  }

  const {
    employment_records = [],
    project_records = [],
    credentials = [],
    capabilities = [],
    contacts = [],
    reviews = [],
  } = stage2Data;

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getRecordTypeIcon = (type) => {
    const icons = {
      EDUCATION: <FaGraduationCap />,
      CERTIFICATION: <FaCertificate />,
      AWARD: <FaAward />,
      PUBLICATION: <FaBook />,
      MEMBERSHIP: <FaUsers />,
      MARINE_COC: <FaStar />,
      MARINE_CDC: <FaIdCard />,
      PASSPORT: <FaIdCard />,
      MEDICAL_FITNESS: <FaShieldAlt />,
      DEFAULT: <FaFileAlt />,
    };
    return icons[type] || icons.DEFAULT;
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: "#28a745",
      APPROVED: "#28a745",
      EXPIRED: "#dc3545",
      REJECTED: "#dc3545",
      ARCHIVED: "#6c757d",
      DRAFT: "#ffc107",
      PENDING: "#ffc107",
      SELF_DECLARED: "#17a2b8",
      EVIDENCE_UPLOADED: "#007bff",
    };
    return colors[status] || "#6c757d";
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      ACTIVE: "badge-active",
      APPROVED: "badge-approved",
      EXPIRED: "badge-expired",
      REJECTED: "badge-rejected",
      ARCHIVED: "badge-archived",
      DRAFT: "badge-draft",
      PENDING: "badge-pending",
      SELF_DECLARED: "badge-info",
      EVIDENCE_UPLOADED: "badge-primary",
    };
    return classes[status] || "badge-default";
  };

  const getStatusIcon = (status) => {
    const icons = {
      ACTIVE: <FaCheckCircle />,
      APPROVED: <FaCheckCircle />,
      EXPIRED: <FaTimesCircle />,
      REJECTED: <FaTimesCircle />,
      ARCHIVED: <FaClock />,
      DRAFT: <FaClock />,
      PENDING: <FaClock />,
    };
    return icons[status] || null;
  };

  // Get unique credential types for filter
  const credentialTypes = [...new Set(credentials.map((c) => c.record_type))];
  const filteredCredentials =
    activeTab === "all"
      ? credentials
      : credentials.filter((c) => c.record_type === activeTab);

  const totalRecords =
    employment_records.length +
    project_records.length +
    credentials.length +
    capabilities.length +
    contacts.length +
    reviews.length;
const isApproved = stage2Data.profile_status === "APPROVED";
const isStage2Submitted = stage2Data.profile_status === "STAGE2_SUBMITTED";
  return (
    <div className="stage2-container">

     {/* Status Approval Buttons - Only show if applicationId is provided and totalrecords is not zero */}
{/* {applicationId && totalRecords !== 0 && (
  <div className="stage2-approval-section">
    <StatusApprovalStage2
      professionalId={professionalId}
      onSuccess={handleRefresh}
    />
  </div>
)} */}

 {isApproved ? (
        <div className="review-banner review-banner--approved">
          <div className="review-banner__icon">
            <FaCheckCircle />
          </div>
          <div className="review-banner__body">
            <h4>
              Stage 2 Approved
            </h4>
            <p>
              Stage 2 professional records have been reviewed and approved.
            </p>
          </div>
        </div>
      ) : (
        applicationId &&
        totalRecords !== 0 && isStage2Submitted &&
          (
          <div className="stage2-approval-section">
            <StatusApprovalStage2
              professionalId={professionalId}
              onSuccess={handleRefresh}
            />
          </div>
        )
      )}



      {/* Employment Records Section */}
      {employment_records.length > 0 && (
        <div
          className={`stage2-section-enhanced ${expandedSections.employment ? "expanded" : ""}`}
        >
          <div
            className="section-header-enhanced"
            onClick={() => toggleSection("employment")}
          >
            <div className="section-header-left">
              <div className="section-icon-enhanced employment">
                <FaBriefcase />
              </div>
              <div className="section-title-group">
                <h3>Employment History</h3>
                <span className="section-subtitle">
                  {employment_records.length} positions
                </span>
              </div>
            </div>
            <div className="section-header-right">
              <span className="section-badge">
                <FaCheckCircle className="badge-icon" />{" "}
                {employment_records.filter((r) => r.status === "ACTIVE").length}{" "}
                Active
              </span>
              <div className="section-toggle">
                {expandedSections.employment ? (
                  <FaChevronUp />
                ) : (
                  <FaChevronDown />
                )}
              </div>
            </div>
          </div>

          {expandedSections.employment && (
            <div className="section-content">
              <div className="timeline-wrapper">
                {employment_records.map((record, index) => (
                  <div className="timeline-item" key={record.id || index}>
                    <div
                      className="timeline-marker"
                      style={{ backgroundColor: getStatusColor(record.status) }}
                    ></div>
                    <div
                      className={`timeline-card ${selectedCard === `emp-${index}` ? "expanded" : ""}`}
                    >
                      <div
                        className="timeline-card-header"
                        onClick={() => toggleCardDetail(`emp-${index}`)}
                      >
                        <div className="card-header-left">
                          <h4>{record.job_title || "Position"}</h4>
                          <span className="employer-name">
                            {record.employer_name_snapshot || "Employer"}
                          </span>
                        </div>
                        <div className="card-header-right">
                          <span
                            className={`status-badge ${getStatusBadgeClass(record.status)}`}
                          >
                            {getStatusIcon(record.status)} {record.status}
                          </span>
                          <span className="card-expand-icon">
                            {selectedCard === `emp-${index}` ? (
                              <FaChevronUp />
                            ) : (
                              <FaChevronDown />
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="timeline-card-body">
                        <div className="info-chips">
                          <span className="chip">
                            <FaMapMarkerAlt /> {record.city},{" "}
                            {record.country_code || "—"}
                          </span>
                          <span className="chip">
                            <FaCalendarAlt /> {formatDate(record.start_date)} -{" "}
                            {record.is_current
                              ? "Present"
                              : formatDate(record.end_date)}
                          </span>
                          {record.is_current && (
                            <span className="chip current">● Current</span>
                          )}
                        </div>
                        {record.duties && (
                          <div className="info-detail">
                            <FaFileAlt className="detail-icon" />
                            <p>{record.duties}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Project Records Section */}
      {project_records.length > 0 && (
        <div
          className={`stage2-section-enhanced ${expandedSections.projects ? "expanded" : ""}`}
        >
          <div
            className="section-header-enhanced"
            onClick={() => toggleSection("projects")}
          >
            <div className="section-header-left">
              <div className="section-icon-enhanced projects">
                <FaUserTie />
              </div>
              <div className="section-title-group">
                <h3>Project Portfolio</h3>
                <span className="section-subtitle">
                  {project_records.length} projects
                </span>
              </div>
            </div>
            <div className="section-header-right">
              <span className="section-badge">
                <FaStar className="badge-icon" />{" "}
                {project_records.filter((p) => p.is_current).length} Active
              </span>
              <div className="section-toggle">
                {expandedSections.projects ? (
                  <FaChevronUp />
                ) : (
                  <FaChevronDown />
                )}
              </div>
            </div>
          </div>

          {expandedSections.projects && (
            <div className="section-content">
              <div className="project-grid">
                {project_records.map((project, index) => (
                  <div
                    className={`project-card ${selectedCard === `proj-${index}` ? "expanded" : ""}`}
                    key={project.id || index}
                  >
                    <div
                      className="project-card-header"
                      onClick={() => toggleCardDetail(`proj-${index}`)}
                    >
                      <div className="project-header-left">
                        <div className="project-icon-wrapper">
                          <FaUserTie />
                        </div>
                        <div className="project-title-group">
                          <h4>{project.project_name || "Project"}</h4>
                          <span className="client-name">
                            {project.client_organization || "Client"}
                          </span>
                        </div>
                      </div>
                      <div className="project-header-right">
                        <span
                          className={`status-badge ${getStatusBadgeClass(project.status)}`}
                        >
                          {getStatusIcon(project.status)} {project.status}
                        </span>
                      </div>
                    </div>
                    <div className="project-card-body">
                      <div className="project-metrics">
                        <div className="metric">
                          <span className="metric-label">Allocation</span>
                          <span className="metric-value">
                            {project.allocation_percent}%
                          </span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Location</span>
                          <span className="metric-value">
                            {project.city}, {project.country_code}
                          </span>
                        </div>
                        <div className="metric">
                          <span className="metric-label">Duration</span>
                          <span className="metric-value">
                            {formatDate(project.start_date)} -{" "}
                            {project.is_current
                              ? "Present"
                              : formatDate(project.end_date)}
                          </span>
                        </div>
                      </div>
                      {project.responsibilities && (
                        <div className="project-description">
                          <p>{project.responsibilities}</p>
                        </div>
                      )}
                      {project.achievements && (
                        <div className="project-achievements">
                          <FaTrophy className="achievement-icon" />
                          <span>{project.achievements}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Credentials Section with Filter */}
      {credentials.length > 0 && (
        <div
          className={`stage2-section-enhanced ${expandedSections.credentials ? "expanded" : ""}`}
        >
          <div
            className="section-header-enhanced"
            onClick={() => toggleSection("credentials")}
          >
            <div className="section-header-left">
              <div className="section-icon-enhanced credentials">
                <FaGraduationCap />
              </div>
              <div className="section-title-group">
                <h3>Credentials & Certifications</h3>
                <span className="section-subtitle">
                  {credentials.length} credentials
                </span>
              </div>
            </div>
            <div className="section-header-right">
              <span className="section-badge">
                <FaMedal className="badge-icon" />{" "}
                {credentials.filter((c) => c.status === "ACTIVE").length} Active
              </span>
              <div className="section-toggle">
                {expandedSections.credentials ? (
                  <FaChevronUp />
                ) : (
                  <FaChevronDown />
                )}
              </div>
            </div>
          </div>

          {expandedSections.credentials && (
            <div className="section-content">
              {credentialTypes.length > 1 && (
                <div className="filter-tabs">
                  <button
                    className={`filter-tab ${activeTab === "all" ? "active" : ""}`}
                    onClick={() => setActiveTab("all")}
                  >
                    All ({credentials.length})
                  </button>
                  {credentialTypes.map((type) => (
                    <button
                      key={type}
                      className={`filter-tab ${activeTab === type ? "active" : ""}`}
                      onClick={() => setActiveTab(type)}
                    >
                      {type} (
                      {credentials.filter((c) => c.record_type === type).length}
                      )
                    </button>
                  ))}
                </div>
              )}
              <div className="credential-grid">
                {filteredCredentials.map((credential, index) => (
                  <div
                    className={`credential-card ${selectedCard === `cred-${index}` ? "expanded" : ""}`}
                    key={credential.id || index}
                  >
                    <div className="credential-card-header">
                      <div className="credential-type-badge">
                        {getRecordTypeIcon(credential.record_type)}
                        <span>{credential.record_type}</span>
                      </div>
                      <span
                        className={`status-badge-small ${getStatusBadgeClass(credential.status)}`}
                      >
                        {credential.status}
                      </span>
                    </div>
                    <div className="credential-card-body">
                      <h4>{credential.title || "Credential"}</h4>
                      <p className="issuer">
                        {credential.issuing_body_snapshot || "Issuer"}
                      </p>
                      <div className="credential-details">
                        {credential.issue_date && (
                          <span className="detail-chip">
                            <FaCalendarAlt /> Issued:{" "}
                            {formatDate(credential.issue_date)}
                          </span>
                        )}
                        {credential.expiry_date && (
                          <span className="detail-chip">
                            <FaClock /> Expires:{" "}
                            {formatDate(credential.expiry_date)}
                          </span>
                        )}
                        {credential.level_or_grade && (
                          <span className="detail-chip">
                            <FaStar /> {credential.level_or_grade}
                          </span>
                        )}
                      </div>
                      {credential.details?.linked_scope && (
                        <div className="credential-scope">
                          <FaLink className="scope-icon" />
                          <span>{credential.details.linked_scope}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Capabilities Section */}
      {capabilities.length > 0 && (
        <div
          className={`stage2-section-enhanced ${expandedSections.capabilities ? "expanded" : ""}`}
        >
          <div
            className="section-header-enhanced"
            onClick={() => toggleSection("capabilities")}
          >
            <div className="section-header-left">
              <div className="section-icon-enhanced capabilities">
                <FaTools />
              </div>
              <div className="section-title-group">
                <h3>Skills & Capabilities</h3>
                <span className="section-subtitle">
                  {capabilities.length} skills
                </span>
              </div>
            </div>
            <div className="section-header-right">
              <span className="section-badge">
                <FaChartLine className="badge-icon" />{" "}
                {capabilities.filter((c) => c.proficiency === "EXPERT").length}{" "}
                Expert
              </span>
              <div className="section-toggle">
                {expandedSections.capabilities ? (
                  <FaChevronUp />
                ) : (
                  <FaChevronDown />
                )}
              </div>
            </div>
          </div>

          {expandedSections.capabilities && (
            <div className="section-content">
              <div className="capabilities-grid">
                {capabilities.map((capability, index) => (
                  <div className="capability-card" key={capability.id || index}>
                    <div className="capability-header">
                      <span className="capability-type">
                        {capability.capability_type}
                      </span>
                      <span
                        className={`proficiency-badge ${capability.proficiency?.toLowerCase() || ""}`}
                      >
                        {capability.proficiency || "N/A"}
                      </span>
                    </div>
                    <h4>{capability.custom_name || "Skill"}</h4>
                    <div className="capability-details">
                      {capability.years_experience && (
                        <span className="detail-chip">
                          <FaClock /> {capability.years_experience} years
                        </span>
                      )}
                      {capability.last_used_date && (
                        <span className="detail-chip">
                          <FaCalendarAlt /> Last used:{" "}
                          {formatDate(capability.last_used_date)}
                        </span>
                      )}
                    </div>
                    {(capability.speaking_level ||
                      capability.reading_level ||
                      capability.writing_level) && (
                      <div className="language-levels">
                        {capability.speaking_level && (
                          <span className="level-badge speaking">
                            <FaLanguage /> Speak: {capability.speaking_level}
                          </span>
                        )}
                        {capability.reading_level && (
                          <span className="level-badge reading">
                            <FaBook /> Read: {capability.reading_level}
                          </span>
                        )}
                        {capability.writing_level && (
                          <span className="level-badge writing">
                            <FaFileAlt /> Write: {capability.writing_level}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contacts Section */}
      {contacts.length > 0 && (
        <div
          className={`stage2-section-enhanced ${expandedSections.contacts ? "expanded" : ""}`}
        >
          <div
            className="section-header-enhanced"
            onClick={() => toggleSection("contacts")}
          >
            <div className="section-header-left">
              <div className="section-icon-enhanced contacts">
                <FaUsers />
              </div>
              <div className="section-title-group">
                <h3>Professional Contacts</h3>
                <span className="section-subtitle">
                  {contacts.length} contacts
                </span>
              </div>
            </div>
            <div className="section-header-right">
              <span className="section-badge">
                <FaCheckCircle className="badge-icon" />{" "}
                {contacts.filter((c) => c.consent_given).length} Consented
              </span>
              <div className="section-toggle">
                {expandedSections.contacts ? (
                  <FaChevronUp />
                ) : (
                  <FaChevronDown />
                )}
              </div>
            </div>
          </div>

          {expandedSections.contacts && (
            <div className="section-content">
              <div className="contacts-grid">
                {contacts.map((contact, index) => (
                  <div className="contact-card" key={contact.id || index}>
                    <div className="contact-avatar">
                      <FaUserCircle />
                    </div>
                    <div className="contact-info">
                      <h4>{contact.full_name || "Contact"}</h4>
                      <span className="contact-type">
                        {contact.contact_type}
                      </span>
                      <div className="contact-details">
                        {contact.relationship && (
                          <span className="detail-chip">
                            <FaUserCircle /> {contact.relationship}
                          </span>
                        )}
                        {contact.organization_name_snapshot && (
                          <span className="detail-chip">
                            <FaBuilding /> {contact.organization_name_snapshot}
                          </span>
                        )}
                      </div>
                      <div className="contact-actions">
                        {contact.email && (
                          <a
                            href={`mailto:${contact.email}`}
                            className="contact-action-btn email"
                          >
                            <FaEnvelope /> Email
                          </a>
                        )}
                        {contact.phone && (
                          <a
                            href={`tel:${contact.phone}`}
                            className="contact-action-btn phone"
                          >
                            <FaPhone /> Call
                          </a>
                        )}
                        <span
                          className={`consent-indicator ${contact.consent_given ? "granted" : "denied"}`}
                        >
                          {contact.consent_given
                            ? "✓ Consent Given"
                            : "✗ No Consent"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <div
          className={`stage2-section-enhanced ${expandedSections.reviews ? "expanded" : ""}`}
        >
          <div
            className="section-header-enhanced"
            onClick={() => toggleSection("reviews")}
          >
            <div className="section-header-left">
              <div className="section-icon-enhanced reviews">
                <FaFileAlt />
              </div>
              <div className="section-title-group">
                <h3>Reviews & Assessments</h3>
                <span className="section-subtitle">
                  {reviews.length} reviews
                </span>
              </div>
            </div>
            <div className="section-header-right">
              <span className="section-badge">
                <FaCheckCircle className="badge-icon" />{" "}
                {reviews.filter((r) => r.decision === "APPROVED").length}{" "}
                Approved
              </span>
              <div className="section-toggle">
                {expandedSections.reviews ? <FaChevronUp /> : <FaChevronDown />}
              </div>
            </div>
          </div>

          {expandedSections.reviews && (
            <div className="section-content">
              <div className="reviews-grid">
                {reviews.map((review, index) => (
                  <div className="review-card" key={review.id || index}>
                    <div className="review-header">
                      <div className="review-type">
                        <FaFileAlt className="review-icon" />
                        <span>{review.review_type || "Review"}</span>
                      </div>
                      <span
                        className={`status-badge ${getStatusBadgeClass(review.decision)}`}
                      >
                        {getStatusIcon(review.decision)} {review.decision}
                      </span>
                    </div>
                    <div className="review-body">
                      <div className="review-meta">
                        <span className="meta-item">
                          <FaCalendarAlt /> {formatDate(review.submitted_at)}
                        </span>
                      </div>
                      {review.reviewer_notes && (
                        <div className="review-notes">
                          <p>{review.reviewer_notes}</p>
                        </div>
                      )}
                      {review.decision_reason && (
                        <div className="review-reason">
                          <strong>Decision Reason:</strong>
                          <p>{review.decision_reason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {totalRecords === 0 && (
        <div className="stage2-empty-state">
          <div className="empty-icon-wrapper">
            <FaFileAlt className="empty-icon" />
          </div>
          <p>No professional records found</p>
          <span className="empty-subtext">
            Complete your profile to showcase your experience
          </span>
        </div>
      )}
    </div>
  );
};

export default UserViewStage2;